<template>
  <div class="slider">
    <div class="images">
      <div class="images__current-image">
        <img
          class="images__image"
          :src="images[activeImage] || placeholderImage"
          :alt="$t('image-slider.car-photo')"
        />
      </div>
      <div v-if="images.length > 1" class="images__next-images">
        <div
          v-for="(img, index) in nextImages"
          :key="img"
          class="images__next-image"
          :style="{ backgroundImage: `url(${img})` }"
        >
          <div
            v-if="index + 1 === nextImages.length && images.length > 4"
            class="images__overlay"
          >
            +{{ imagesLeft }}
          </div>
        </div>
      </div>
    </div>
    <div v-if="images.length > 1" class="slider__counter">
      {{ imagesCounter }}
    </div>
    <div v-if="images.length > 1" class="arrows">
      <button
        :aria-label="$t('image-slider.previous-image')"
        class="arrows__arrow"
        @click="previousImage"
      >
        <left-arrow />
      </button>
      <button
        :aria-label="$t('image-slider.next-image')"
        class="arrows__arrow"
        @click="nextImage"
      >
        <right-arrow />
      </button>
    </div>
    <base-price-badges
      :monthly-price="monthlyPrice"
      :car-price="carPrice"
      class="slider__price"
    />
  </div>
</template>

<script>
import BasePriceBadges from "@/components/ui/BasePriceBadges/BasePriceBadges.vue";
import LeftArrow from "@/assets/images/left-arrow.svg";
import RightArrow from "@/assets/images/right-arrow.svg";
import placeholderImage from "@/assets/images/cars/placeholder.png";

export default {
  name: "BaseImageSlider",
  components: {
    LeftArrow,
    RightArrow,
    BasePriceBadges
  },
  props: {
    images: {
      type: Array,
      default: () => []
    },
    monthlyPrice: {
      type: Number,
      default: 0
    },
    carPrice: {
      type: Number,
      default: 0
    }
  },
  data() {
    return {
      activeImage: 0
    };
  },
  computed: {
    imagesCounter() {
      return `${this.activeImage + 1}/${this.images.length}`;
    },
    imagesLeft() {
      const alreadyVisibleImages = 3;
      return this.images.length - alreadyVisibleImages;
    },
    nextImages() {
      const nextImagesIndex = Array.from(
        { length: this.images.length > 3 ? 3 : this.images.length },
        (value, i) => this.activeImage + 1 + i
      );
      return nextImagesIndex.map(index => this.getNextItem(index));
    },
    placeholderImage() {
      return placeholderImage;
    }
  },
  methods: {
    previousImage() {
      this.activeImage =
        this.activeImage > 0 ? this.activeImage - 1 : this.images.length - 1;
    },
    nextImage() {
      this.activeImage =
        this.activeImage < this.images.length - 1 ? this.activeImage + 1 : 0;
    },
    getNextItem(currentItem) {
      return currentItem < this.images.length
        ? this.images[this.images.indexOf(this.images[currentItem])]
        : currentItem - this.images.length !== this.activeImage &&
          this.images[currentItem - this.images.length];
    }
  }
};
</script>

<style scoped lang="scss">
.slider {
  position: relative;
  overflow: hidden;

  &__counter {
    @include absolute(top 13px right 13px);

    font-size: $font-size-s;
    background-color: rgba(0, 0, 0, .6);
    color: $color-white;
    padding: 5px;
  }

  &__price {
    @include absolute(bottom 0);
  }
}

.images {
  line-height: 0;

  &__image {
    width: 100%;
  }

  &__next-images {
    display: none;
  }
}

.arrows {
  @include absolute(top 40% left 0 right 0);

  display: flex;
  justify-content: space-between;
  transform: translate(0, -50%);

  &__arrow {
    @include sizing(40px);
    @include flex-centered();

    background-color: rgba(0, 0, 0, .6);
  }
}

@media screen and (min-width: $media-l) {
  .slider {
    overflow: visible;

    &__price {
      display: none;
    }
  }

  .images {
    display: flex;
    flex-direction: column;

    &__current-image {
      max-width: 100%;
      position: relative;

      &[src=""]::before {
        display: block;
        content: "";
        padding-top: calc(100% * 2 / 3);
      }
    }

    &__next-image {
      position: relative;
      background-size: cover;
      background-position: center;
    }

    &__next-images {
      @include grid-columns(3);
      @include grid-ie11-fix(3);

      height: 150px;
    }

    &__with-overlay {
      position: relative;
    }

    &__overlay {
      @include flex-centered();
      @include absolute(top 0 right 0 bottom 0 left 0);

      background-color: rgba(0, 0, 0, .6);
      color: $color-white;
      font-size: $font-size-xxl;
    }
  }
}

@media screen and (min-width: $media-xxxl) {
  .images__next-images {
    height: 230px;
  }
}

@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
  .images__current-image {
    height: 100%;
  }
}
</style>
